'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import { useMemo, type ReactNode } from 'react';

import {
  AuthNavigationProvider,
  type AuthNavigation,
} from '@eliadmin/core/src/components/dlz/auth/dialog/auth-navigation';
import { REGISTER_SUCCESS_PARAM } from '@eliadmin/core/src/components/dlz/auth/register/register-copy';
import { ROUTES } from '@eliadmin/core/src/constants/routes';

/** `?returnUrl=` (core `ROUTES.SIGN_IN(returnUrl)`) only when it is a path of this site. */
function safeReturnUrl(value: string | null) {
  if (!value || !value.startsWith('/') || value.startsWith('//')) return null;
  if (/[\\\u0000-\u001F\u007F]/.test(value)) return null;
  return value;
}

/**
 * Navigation of the sign-in and registration pages (the forms are the same as in the dialog):
 * moving between the views goes to the page of that view, a signed-in customer returns to
 * `returnUrl` (the checkout's „Prihlásiť sa“) or lands in the profile.
 */
export function PageAuthNavigation({ children }: { children: ReactNode }) {
  const router = useRouter();
  const returnUrl = safeReturnUrl(useSearchParams().get('returnUrl'));

  const navigation = useMemo<AuthNavigation>(
    () => ({
      showLogin: () => router.push(ROUTES.SIGN_IN(returnUrl ?? undefined)),
      showRegister: () => router.push(ROUTES.REGISTER(returnUrl ?? undefined)),
      showPasswordReset: () => router.push(ROUTES.PASSWORD_RESET()),
      showRegistered: () => router.replace(`${ROUTES.REGISTER()}?${REGISTER_SUCCESS_PARAM}`),
      signedIn: () => router.replace(returnUrl ?? ROUTES.PROFILE()),
      close: () => router.push(ROUTES.SIGN_IN()),
    }),
    [returnUrl, router],
  );

  return <AuthNavigationProvider value={navigation}>{children}</AuthNavigationProvider>;
}
